import { Vector3 } from "three";
import type { Engine } from "../../../../engine/Engine";
import {
  raycastOnPlaneFromScreen,
  tryRaycastOnPlaneFromScreen,
} from "../../../../physics/PhysicsUtil";
import { flattenY } from "../../../../shape/segment/computeSegmentBox";
import { pathNormalAt, pathPointAt } from "../../../../shape/segment/axis/pathHelpers";
import type { SegmentPath, XzPoint } from "../../../../shape/segment/types";

const FLOOR_PLANE = {
  position: new Vector3(0, 0, 0),
  normal: new Vector3(0, 1, 0),
};

const _wallPlane = {
  position: new Vector3(),
  normal: new Vector3(),
};

export function pointerHitOnFloor(
  event: PointerEvent,
  engine: Engine,
): Vector3 {
  return raycastOnPlaneFromScreen(
    event,
    FLOOR_PLANE,
    engine.activeCamera!.instance,
    engine.canvas!,
  );
}

export function pointerXzOnFloor(
  event: PointerEvent,
  engine: Engine,
): XzPoint {
  return flattenY(pointerHitOnFloor(event, engine));
}

/**
 * 3D opening slide: ray vs vertical plane through the host centerline
 * (path tangent × up). Falls back to the floor if the ray is parallel.
 */
export function pointerXzOnHostWall(
  event: PointerEvent,
  engine: Engine,
  hostPath: SegmentPath,
  t = 0.5,
): XzPoint {
  const camera = engine.activeCamera?.instance;
  const dom = engine.canvas;
  if (!camera || !dom) return pointerXzOnFloor(event, engine);
  const point = pathPointAt(hostPath, t);
  const normal = pathNormalAt(hostPath, t);
  _wallPlane.position.set(point.x, 0, point.z);
  _wallPlane.normal.set(normal.x, 0, normal.z);
  if (_wallPlane.normal.lengthSq() < 1e-10) {
    return pointerXzOnFloor(event, engine);
  }
  _wallPlane.normal.normalize();
  const hit = tryRaycastOnPlaneFromScreen(event, _wallPlane, camera, dom);
  return hit ? flattenY(hit) : pointerXzOnFloor(event, engine);
}

export function isPointerOverCanvas(
  event: PointerEvent,
  engine: Engine,
): boolean {
  const canvas = engine.canvas;
  if (!canvas) return false;
  const rect = canvas.getBoundingClientRect();
  return (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  );
}
